import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { init, useFocusable, FocusContext } from "@noriginmedia/norigin-spatial-navigation";
import { useEvents } from "./context/EventsContext";
import { getEPGFirstFocusKey, safeSetFocus } from "./utils/shared/focus";
import { EVENT_STATUS, EVENT_TITLES, SECTION_TITLES, SECTION_TYPES } from "./constants/event";
import { capitalizeFirstLetter } from "./utils/shared/string";
import EPGSectionRow from "./EPGSectionRow";
import EPGFilteredEventsRow from "./EPGFilteredSectionRow";
import StreamingReadinessBanner from "../states/readiness/StreamingReadinessBanner";
import sortEvents from './utils/shared/sortEvents';
import "./epg.scss";

init({
    debug: false,
    visualDebug: false,
});

const SHORTCUT_VPARAMS = { cardWidth: 168, cardGap: 24, amountOfCardsInWindow: 15 };
const EVENTS_VPARAMS   = {};

const getRowsData = (liveEvents, channels, upcomingEvents, scheduledEvents, { sportsData, leaguesData, partnersData, playingNextEvents }) => {
    return [
        {
            render: !!playingNextEvents.length,
            rowType: SECTION_TYPES.PLAYING_NEXT,
            rowId: "playing-next",
            title: SECTION_TITLES.NEXT_ON_THIS_SCREEN,
            items: playingNextEvents,
            virtualizationParams: EVENTS_VPARAMS,
        },
        {
            render: !!partnersData.length,
            rowType: SECTION_TYPES.PARTNERS,
            rowId: "partners",
            title: EVENT_TITLES[SECTION_TYPES.PARTNERS],
            items: partnersData,
            virtualizationParams: SHORTCUT_VPARAMS,
        },
        {
            render: !!liveEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "live",
            title: EVENT_TITLES[EVENT_STATUS.LIVE],
            items: liveEvents,
            virtualizationParams: EVENTS_VPARAMS,
        },
        {
            render: !!sportsData.length,
            rowType: SECTION_TYPES.SPORTS,
            rowId: "sports",
            title: EVENT_TITLES[SECTION_TYPES.SPORTS],
            items: sportsData,
            virtualizationParams: SHORTCUT_VPARAMS,
        },
        {
            render: !!leaguesData.length,
            rowType: SECTION_TYPES.LEAGUES,
            rowId: "leagues",
            title: EVENT_TITLES[SECTION_TYPES.LEAGUES],
            items: leaguesData,
            virtualizationParams: SHORTCUT_VPARAMS,
        },
        {
            render: !!channels.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "channels",
            title: SECTION_TITLES.CHANNELS,
            items: channels,
            virtualizationParams: EVENTS_VPARAMS,
        },
        {
            render: !!upcomingEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "upcoming",
            title: EVENT_TITLES[EVENT_STATUS.COMING],
            items: upcomingEvents,
            virtualizationParams: EVENTS_VPARAMS,
        },
        {
            render: !!scheduledEvents.length,
            rowType: SECTION_TYPES.EVENTS,
            rowId: "scheduled",
            title: EVENT_TITLES[EVENT_STATUS.SCHEDULED],
            items: scheduledEvents,
            virtualizationParams: EVENTS_VPARAMS,
        },
    ];
};

const EPGContent = ({ sportsData, leaguesData, partnersData, onEnterPress, deeplinkEvent, shouldApplyComcastStyles, onWatchNow, onRunReadinessTest, readinessIsSubscribed, readinessIsLoading, subscribingEventId, onPlayingNextPlay }) => {
    const { liveEvents, upcomingEvents, scheduledEvents, channels, showFiltered, selectedSport, handleFilterSelect, filteredEvents: getFilteredEvents, activeLiveContent, playingNextEvents } = useEvents();

    const enrichedPartners = useMemo(() => {
        return partnersData.map((partner) => {
            const matchingChannel = channels.find(
                (ch) => ch.subscriptionType?.backwards_compatible_name === partner.searchTerm
            );
            if (!matchingChannel) return partner;
            return {
                ...partner,
                channelData: {
                    bannerUrl: matchingChannel.subscriptionType?.banner_url,
                    channelName: matchingChannel.title,
                    channelId: matchingChannel.id,
                },
            };
        });
    }, [partnersData, channels]);

    const sortedLiveEvents = useMemo(() => {
        return sortEvents(liveEvents, activeLiveContent);
    }, [liveEvents, activeLiveContent]);

    const { ref, focusKey } = useFocusable({
        focusKey: "epg-content-container",
    });

    const rowsData = useMemo(
        () => getRowsData(sortedLiveEvents, channels, upcomingEvents, scheduledEvents, { sportsData, leaguesData, partnersData: enrichedPartners, playingNextEvents }),
        [sortedLiveEvents, channels, upcomingEvents, scheduledEvents, sportsData, leaguesData, enrichedPartners, playingNextEvents]
    );


    // When entering filtered view, reset scroll to top so the banner/title is visible
    // (scrollingRows retains its scroll position from the main view otherwise)
    const scrollingRowsRef = useRef(null);

    useEffect(() => {
        if (showFiltered && scrollingRowsRef.current) {
            scrollingRowsRef.current.scrollTop = 0;
        }
    }, [showFiltered]);

    // Initial focus: the EPG can open before events load (cold start), so retry once data arrives
    const hasInitialFocus = useRef(false);

    useEffect(() => {
        if (showFiltered) {
            hasInitialFocus.current = false;
            return;
        }
        if (deeplinkEvent) {
            // The deeplink flow owns focus; clearing it later must not trigger initial focus
            hasInitialFocus.current = true;
            return;
        }
        if (hasInitialFocus.current) return;
        // Resolve the key when the timeout fires: events may arrive after mount (cold start)
        const timeoutId = setTimeout(() => {
            const focusKey = getEPGFirstFocusKey(rowsData);
            if (focusKey) {
                safeSetFocus(focusKey);
                hasInitialFocus.current = true;
            }
        }, 50);
        return () => clearTimeout(timeoutId);
    }, [showFiltered, rowsData, deeplinkEvent]);

    const handleEnterPress = useCallback(
        (focusProps, rowType) => {
            const { focusKey } = focusProps;

            switch (rowType) {
                case SECTION_TYPES.SPORTS:
                    const sport = sportsData.find((s) => String(s.id) === String(focusKey));
                    if (sport) handleFilterSelect(sport, { type: "sport" });
                    break;
                case SECTION_TYPES.LEAGUES:
                    const league = leaguesData.find((l) => String(l.id) === String(focusKey));
                    if (league) handleFilterSelect(league, { type: "league" });
                    break;
                case SECTION_TYPES.PARTNERS:
                    const partner = enrichedPartners.find((p) => String(p.id) === String(focusKey));
                    if (partner) handleFilterSelect(partner, { type: "partner" });
                    break;
                default:
                    onEnterPress(focusProps);
            }
        },
        [onEnterPress, sportsData, leaguesData, enrichedPartners, handleFilterSelect]
    );

    const renderMainContent = () => {
        const visibleRows = rowsData.filter((row) => row.render);
        const partnersIdx = visibleRows.findIndex((r) => r.rowId === 'partners');
        const splitAt = partnersIdx >= 0 ? partnersIdx + 1 : 0;

        const renderRow = (row, index) => (
            <EPGSectionRow
                key={row.rowId}
                rowType={row.rowType}
                rowId={row.rowId}
                title={row.title}
                items={row.items}
                virtualizationParams={row.virtualizationParams}
                onEnterPress={handleEnterPress}
                activeLiveContentId={activeLiveContent?.id ?? null}
                activeLiveContentType={activeLiveContent?.contentType ?? null}
                subscribingEventId={row.rowType === SECTION_TYPES.PLAYING_NEXT ? subscribingEventId : null}
                onPlayingNextPlay={onPlayingNextPlay}
            />
        );

        return (
            <div>
                {visibleRows.slice(0, splitAt).map(renderRow)}
                {onRunReadinessTest && <StreamingReadinessBanner onRunTest={onRunReadinessTest} isSubscribed={readinessIsSubscribed} isLoading={readinessIsLoading} />}
                {visibleRows.slice(splitAt).map(renderRow)}
            </div>
        );
    };

    // Render the filtered events
    const renderFilteredContent = () => {
        const channelData = selectedSport?.channelData || null;
        const rawTitle = selectedSport?.name || selectedSport?.alias || "Filtered Events";
        const title = channelData ? "Live and Upcoming Events" : `${capitalizeFirstLetter(rawTitle)} Events`;
        const filteredEvents = getFilteredEvents();

        return (
            <EPGFilteredEventsRow
                events={filteredEvents}
                title={title}
                onEnterPress={onEnterPress}
                shouldApplyComcastStyles={shouldApplyComcastStyles}
                channelData={channelData}
                onWatchNow={onWatchNow}
            />
        );
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="containerWrapper">
                <div ref={scrollingRowsRef} className="scrollingRows">{showFiltered ? renderFilteredContent() : renderMainContent()}</div>
            </div>
        </FocusContext.Provider>
    );
};
export default EPGContent;
